import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { useUser } from "../../hooks/useUser";
import KpiMetric from "./components/KpiMetric";
import DashboardKpis from "./components/DashboardKpis";
import ActiveProjects from "./components/ActiveProjects";
import Experts from "./components/Experts";
import FeaturedExperts from "./components/FeaturedExperts";
import Products from "./components/Products";
import Videos from "./components/Videos";
import TeamMembers from "./components/TeamMembers";
import ToDoList, { useDashboardChecklist } from "./components/ToDoList";
import VideoShowcase from "./components/VideoShowcase";
import ActivityFeed from "../../components/ActivityFeed";
import axiosInstant from "../../api";
import {
    getAllBrandProjects,
    getBrandProjects,
    getBrandOrders,
    getBrandOrdersByBrand,
    getVideoEndpoint
} from "../../api/endpoints";
import { useWorkspaceBrandId } from "../../hooks/useWorkspaceBrandId";
import { useNetworkScope } from "../../hooks/useNetworkScope";
import { isInternal } from "../../utils";

// Cards grow to their content and only cap — never a fixed height. Content
// past this ceiling scrolls inside the card (each child owns its own scroll
// region). CSS grid + align-items:stretch then aligns the two cards in a row.
const CARD_MAX_HEIGHT = 350;

const cardSx = {
    minWidth: 0,
    boxSizing: "border-box",
    width: "100%",
    maxHeight: `${CARD_MAX_HEIGHT}px`,
    overflow: "hidden", // clip to the rounded card; the child owns any scroll
    padding: { xs: "16px", md: "15px 28px" },
    borderRadius: { xs: "12px", md: "16px" },
    display: "flex",
    flexDirection: "column",
    // The single child fills the card (at every breakpoint) so its internal
    // header/list can flex and scroll against the card's height.
    "& > *": {
        minWidth: 0,
        minHeight: 0,
        width: "100%",
        flex: "1 1 auto"
    }
};

const Dashboard: React.FC = () => {
    const { user, role } = useUser();
    // Internal staff inside a client workspace see THAT brand's numbers.
    const workspaceBrandId = useWorkspaceBrandId();
    const { brandId: scopeBrandId } = useNetworkScope();
    // The To Do list: the brand's data plus the user's own ticks/dismissal.
    // Brand-side users only; internal staff never get it (brand id 0 = no
    // request, no card), workspace or not.
    const checklist = useDashboardChecklist(isInternal(role) ? 0 : scopeBrandId);
    const [activeProjects, setActiveProjects] = useState(0);
    const [completedProjects, setCompletedProjects] = useState(0);
    const [totalProducts, setTotalProducts] = useState(0);
    const [totalVideos, setTotalVideos] = useState(0);
    const [totalOrders, setTotalOrders] = useState(0);
    const [refreshKey, setRefreshKey] = useState(0);
    const [totalExperts, setTotalExperts] = useState(0);
    const [hasVideos, setHasVideos] = useState(false);
    const [hasExperts, setHasExperts] = useState(false);
    const [hasFeaturedExperts, setHasFeaturedExperts] = useState(false);
    const [greeting, setGreeting] = useState("");

    const welcomeGreetings = ["Welcome", "Howdy", "Hi there", "What's up", "Good to see you"];

    useEffect(() => {
        const randomGreeting = welcomeGreetings[Math.floor(Math.random() * welcomeGreetings.length)];
        setGreeting(randomGreeting);
    }, []);

    useEffect(() => {
        const fetchCompleted = async () => {
            try {
                const brandId = workspaceBrandId || user?.brand?.[0]?.id;
                let response;
                if (brandId) {
                    response = await axiosInstant.get(`${getBrandProjects(brandId)}?status=complete&limit=1`);
                } else {
                    response = await axiosInstant.get(`${getAllBrandProjects()}?status=complete&limit=1`);
                }
                const count = response?.data?.count ?? 0;
                setCompletedProjects(count);
            } catch (e) {
                setCompletedProjects(0);
            }
        };
        fetchCompleted();
    }, [user?.brand]);

    const fetchOrders = useCallback(async () => {
        try {
            const brandId = workspaceBrandId || user?.brand?.[0]?.id;
            let response;
            if (brandId) {
                response = await axiosInstant.get(getBrandOrdersByBrand(brandId));
            } else {
                response = await axiosInstant.get(getBrandOrders());
            }
            const data = response?.data;
            setTotalOrders(data?.count ?? data?.length ?? 0);
        } catch {
            setTotalOrders(0);
        }
    }, [user?.brand]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    useEffect(() => {
        const checkForVideos = async () => {
            try {
                const brandId = workspaceBrandId || user?.brand?.[0]?.id;
                if (brandId) {
                    // Use the same endpoint as the Videos component
                    const endpoint = getVideoEndpoint(brandId.toString(), "TDC", {
                        pagination: { offset: 0, limit: 1 }
                    });
                    const response = await axiosInstant.get(endpoint);
                    setHasVideos(response.data?.count > 0);
                } else {
                    setHasVideos(false);
                }
            } catch (error) {
                console.error("Error checking for videos:", error);
                setHasVideos(false);
            }
        };

        if (user) {
            checkForVideos();
        }
    }, [user]);

    useEffect(() => {
        const checkForExperts = async () => {
            try {
                const brandId = workspaceBrandId || user?.brand?.[0]?.id;
                if (brandId) {
                    const response = await axiosInstant.get(`/brands/${brandId}/pros?page=0&limit=1`);
                    setHasExperts((response.data?.count ?? 0) > 0);
                } else {
                    setHasExperts(false);
                }
            } catch (error) {
                console.error("Error checking for experts:", error);
                setHasExperts(false);
            }
        };

        if (user) {
            checkForExperts();
        }
    }, [user]);

    useEffect(() => {
        const checkForFeaturedExperts = async () => {
            try {
                const response = await axiosInstant.get("/users/pros", {
                    params: { featured: "true", offset: 0, limit: 1 }
                });
                setHasFeaturedExperts((response.data?.count ?? 0) > 0);
            } catch (error) {
                setHasFeaturedExperts(false);
            }
        };

        if (user) {
            checkForFeaturedExperts();
        }
    }, [user]);

    // While the To Do card hides, Active Projects takes the whole row
    // (span 2) and lays its projects out two-up (wide mode).
    const showTodo = checklist.show;

    return (
        <Box className="pt-6 px-3 sm:pt-12 sm:px-6">
            <Typography
                className="!text-[32px] !leading-[40px] !tracking-[0%] !font-black !text-text-1 max-md:!text-[22px] max-md:!leading-[28px]"
                sx={{
                    color: "var(--Base-1000, #000)",
                    fontFamily: "CircularStd",
                    fontStyle: "normal",
                    fontWeight: 900,
                    overflowWrap: "anywhere"
                }}
            >
                {greeting}, {user?.firstName || user?.displayName || "User"}!
            </Typography>

            <DashboardKpis
                activeProjects={activeProjects}
                completedProjects={completedProjects}
                totalProducts={totalProducts}
                totalVideos={totalVideos}
                totalOrders={totalOrders}
            />

            <Box
                className="mt-[15px] bg-surface shadow-sm border border-border-token"
                sx={{
                    borderRadius: { xs: "12px", md: "16px" },
                    padding: { xs: "16px", md: "15px 28px" }
                }}
            >
                {/* Single-brand user → anchor to that brand. Internal users (no brand)
            AND multi-brand users → 'global' with no id: the backend scopes the
            feed to the requester's accessible brands, so a multi-brand user sees
            ALL their brands' activity instead of only brand[0]. */}
                <ActivityFeed
                    level={(user?.brand?.length ?? 0) === 1 ? "brand" : "global"}
                    id={(user?.brand?.length ?? 0) === 1 ? user?.brand?.[0]?.id : undefined}
                    title="Recent activity"
                    filters="full"
                    disableContainer
                    maxHeight={280}
                    pollMs={60000}
                />
            </Box>

            <Box className="mt-[15px] grid grid-cols-2 gap-4 w-full auto-rows-max max-md:grid-cols-1 max-md:gap-3 max-md:min-w-0">
                <Box
                    sx={showTodo ? cardSx : { ...cardSx, gridColumn: { md: "span 2" } }}
                    className="bg-surface shadow-sm border border-border-token"
                >
                    <ActiveProjects key={refreshKey} limit={20} wide={!showTodo} onCount={(c) => setActiveProjects(c)} />
                </Box>

                {showTodo && (
                    <Box sx={cardSx} className="bg-surface shadow-sm border border-border-token">
                        <ToDoList
                            items={checklist.items}
                            checked={checklist.checked}
                            onToggle={checklist.toggle}
                            onDismiss={checklist.dismiss}
                            onOrderCreated={() => {
                                fetchOrders();
                                checklist.refresh();
                                setRefreshKey((k) => k + 1);
                            }}
                        />
                    </Box>
                )}

                {hasFeaturedExperts && (
                    <Box
                        sx={{ ...cardSx, maxHeight: "400px", gridColumn: { xs: "span 1", md: "span 2" } }}
                        className="bg-surface shadow-sm border border-border-token"
                    >
                        <FeaturedExperts limit={8} />
                    </Box>
                )}

                <Box sx={{ ...cardSx, maxHeight: "400px" }} className="bg-surface shadow-sm border border-border-token">
                    <Products onCount={(c) => setTotalProducts(c)} onProductAdded={checklist.refresh} />
                </Box>

                <Box sx={{ ...cardSx, maxHeight: "400px" }} className="bg-surface shadow-sm border border-border-token">
                    <TeamMembers />
                </Box>

                {hasExperts && (
                    <Box sx={{ ...cardSx, maxHeight: "400px" }} className="bg-surface shadow-sm border border-border-token">
                        <Experts limit={8} onCount={(c) => setTotalExperts(c)} />
                    </Box>
                )}

                {hasVideos && (
                    <Box sx={{ ...cardSx, maxHeight: "400px" }} className="bg-surface shadow-sm border border-border-token">
                        <Videos onCount={(c) => setTotalVideos(c)} />
                    </Box>
                )}
            </Box>
            <VideoShowcase />
        </Box>
    );
};

export default Dashboard;
